import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Pencil, Trash2, Archive, ArchiveRestore, Search, X as XIcon, Star, ExternalLink, Tag, Download, Upload, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AddressFields, EMPTY_ADDRESS } from "@/components/AddressFields";
import { TagPicker, TagFilterPopover, TagManagerDialog, BulkTagDialog } from "@/components/contacts/TagSelectors";

const PRONOUN_OPTIONS = ["she/her", "he/him", "they/them", "she/they", "he/they", "ze/zir"];

export default function Contacts() {
  const { currentWorkspace } = useWorkspace();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [search, setSearch] = useState("");

  // Tag UX state — catalog drives filter chips + edit-dialog picker,
  // `tagFilter` drives client-side filtering of the contacts table.
  const [tagCatalog, setTagCatalog] = useState([]);
  const [tagFilter, setTagFilter] = useState([]);
  const [tagManagerOpen, setTagManagerOpen] = useState(false);

  // Sort — mirrors the Clients-tab sort dropdown so the two pages
  // behave identically. LTV is omitted because plain contacts don't
  // carry lifetime_value_minor (it's a client-only rollup).
  const [sort, setSort] = useState("recently_added");

  // Multi-select state for bulk delete.
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkTagOpen, setBulkTagOpen] = useState(false);

  // Import / template-download state.
  const [importOpen, setImportOpen] = useState(false);

  // Archive view filter — current | archived | all. Default to "current"
  // so the page still opens to the visible-everyday contacts. Archived
  // rows surface when the host wants to revive someone they previously
  // soft-deleted.
  const [view, setView] = useState("current");

  const load = async () => {
    if (!currentWorkspace) return;
    const { data } = await api.get(`/contacts`, {
      params: { workspace_id: currentWorkspace.workspace_id, view },
    });
    setList(data);
  };
  const loadTags = async () => {
    if (!currentWorkspace) return;
    try {
      const r = await api.get(`/booking/tags`, { params: { workspace_id: currentWorkspace.workspace_id } });
      setTagCatalog(r.data || []);
    } catch { setTagCatalog([]); }
  };
  useEffect(() => { load(); loadTags(); /* eslint-disable-next-line */ }, [currentWorkspace, view]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const tagSet = new Set(tagFilter);
    const rows = list.filter((c) => {
      if (q) {
        const fields = [c.name, c.email, c.company, c.internal_notes, c.source];
        if (!fields.some((f) => String(f || "").toLowerCase().includes(q))) return false;
      }
      if (tagSet.size > 0) {
        const contactTags = Array.isArray(c.tags) ? c.tags : [];
        // OR semantics: contact matches if it has ANY of the selected tags.
        if (!contactTags.some((t) => tagSet.has(t))) return false;
      }
      return true;
    });

    // Client-side sort — the GET /contacts endpoint always returns
    // `created_at desc`; we re-sort locally to keep the two pages in
    // sync without an extra backend round-trip.
    const cmpStr = (a, b) =>
      String(a || "").localeCompare(String(b || ""), undefined, { sensitivity: "base" });
    const fn = (c) => c.first_name || (c.name || "").split(" ")[0] || "";
    const ln = (c) => c.last_name || ((c.name || "").split(" ").slice(1).join(" ")) || "";
    // Primary tag fallback — sort by the contact's first tag (sorted
    // alphabetically so multi-tag contacts use a deterministic key).
    const primaryTag = (c) => {
      const arr = Array.isArray(c.tags) ? c.tags.slice().sort((x, y) => cmpStr(x, y)) : [];
      return arr[0] || "";
    };
    const sorted = rows.slice();
    switch (sort) {
      case "first_asc":  sorted.sort((a, b) => cmpStr(fn(a), fn(b)) || cmpStr(ln(a), ln(b))); break;
      case "first_desc": sorted.sort((a, b) => cmpStr(fn(b), fn(a)) || cmpStr(ln(b), ln(a))); break;
      case "last_asc":   sorted.sort((a, b) => cmpStr(ln(a), ln(b)) || cmpStr(fn(a), fn(b))); break;
      case "last_desc":  sorted.sort((a, b) => cmpStr(ln(b), ln(a)) || cmpStr(fn(b), fn(a))); break;
      case "email_asc":  sorted.sort((a, b) => cmpStr(a.email, b.email)); break;
      case "email_desc": sorted.sort((a, b) => cmpStr(b.email, a.email)); break;
      case "tag_asc":    sorted.sort((a, b) => cmpStr(primaryTag(a), primaryTag(b))); break;
      case "tag_desc":   sorted.sort((a, b) => cmpStr(primaryTag(b), primaryTag(a))); break;
      case "recently_updated":
        sorted.sort((a, b) => String(b.updated_at || "").localeCompare(String(a.updated_at || "")));
        break;
      case "recently_added":
      default:
        // Server returned `created_at desc` already — keep order, but
        // still normalise for consistency.
        sorted.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
        break;
    }
    return sorted;
  }, [list, search, tagFilter, sort]);

  // Clear selection whenever the visible result set changes.
  useEffect(() => { setSelected(new Set()); }, [search, tagFilter, sort]);

  const del = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    await api.delete(`/contacts/${c.contact_id}`);
    toast.success("Contact deleted");
    load();
  };
  const archive = async (c) => {
    await api.patch(`/contacts/${c.contact_id}`, { is_archived: true });
    toast.success("Contact archived");
    load();
  };
  // Restore a previously archived contact — toggles is_archived back
  // to false. Surfaced as a small button on archived rows so hosts can
  // revive a contact without a database round-trip.
  const restore = async (c) => {
    await api.patch(`/contacts/${c.contact_id}`, { is_archived: false });
    toast.success("Contact restored");
    load();
  };
  // Toggle the soft is_client flag in place — surfaces as a star icon
  // per row so hosts can promote/demote without opening the Clients tab.
  const toggleClient = async (c) => {
    const ws = currentWorkspace?.workspace_id;
    if (!ws) return;
    try {
      await api.post(`/booking/clients/${c.contact_id}/${c.is_client ? "demote" : "promote"}`, null, { params: { workspace_id: ws } });
      toast.success(c.is_client ? "Removed client flag" : "Marked as client");
      load();
    } catch {
      toast.error("Could not update");
    }
  };

  const bulkDelete = async () => {
    const n = selected.size;
    if (!n) return;
    if (!window.confirm(`Delete ${n} contact${n === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post(`/contacts/bulk/delete`,
        { contact_ids: Array.from(selected) },
        { params: { workspace_id: currentWorkspace?.workspace_id } });
      toast.success(`Deleted ${r.data?.deleted || 0}`);
      setSelected(new Set());
      load();
    } catch {
      toast.error("Could not bulk-delete");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-6" data-testid="contacts-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="bloq-eyebrow">Contacts</p>
          <h1 className="bloq-headline text-4xl sm:text-5xl mt-2">People you've met with.</h1>
          <p className="text-sm text-[#4a4a52] mt-2">
            Star a contact to mark them as a client and unlock the extended profile (tags, address, lifetime value, booking history).{" "}
            <Link to="/app/bookings?tab=clients" className="text-[#005e7a] underline inline-flex items-center gap-1" data-testid="contacts-go-clients">
              Open Clients tab <ExternalLink className="w-3 h-3" />
            </Link>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={async () => {
            try {
              const res = await api.get("/contacts/import/template", {
                params: { workspace_id: currentWorkspace?.workspace_id },
                responseType: "blob",
              });
              const url = window.URL.createObjectURL(res.data);
              const a = document.createElement("a");
              a.href = url;
              a.download = "Bloq_Contacts_Import_Template.xlsx";
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(url);
            } catch {
              toast.error("Could not download template");
            }
          }} className="bloq-btn-secondary inline-flex items-center gap-2 text-sm" data-testid="download-template-button">
            <Download className="w-4 h-4" /> Template
          </button>
          <button onClick={async () => {
            try {
              const res = await api.get("/contacts/export/xlsx", {
                params: { workspace_id: currentWorkspace?.workspace_id },
                responseType: "blob",
              });
              const url = window.URL.createObjectURL(res.data);
              const a = document.createElement("a");
              a.href = url;
              const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
              a.download = `Bloq_Contacts_Export_${stamp}.xlsx`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(url);
              toast.success("Export downloaded");
            } catch {
              toast.error("Could not export contacts");
            }
          }} className="bloq-btn-secondary inline-flex items-center gap-2 text-sm" data-testid="export-contacts-button">
            <Download className="w-4 h-4 rotate-180" /> Export
          </button>
          <button onClick={() => setImportOpen(true)} className="bloq-btn-secondary inline-flex items-center gap-2 text-sm" data-testid="import-contacts-button">
            <Upload className="w-4 h-4" /> Import
          </button>
          <button onClick={() => { setEditing(null); setShowDialog(true); }} className="bloq-btn-primary inline-flex items-center gap-2" data-testid="new-contact-button">
            <Plus className="w-4 h-4" /> Add contact
          </button>
        </div>
      </header>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md min-w-[200px]">
          <Search className="w-4 h-4 text-[#82828c] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search contacts by name, email, or company…"
            className="bloq-input pl-9 pr-9 w-full"
            data-testid="contacts-search-input"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-[#f5efe6]"
              data-testid="contacts-search-clear"
              aria-label="Clear search"
            >
              <XIcon className="w-3.5 h-3.5 text-[#82828c]" />
            </button>
          )}
        </div>
        <TagFilterPopover
          catalog={tagCatalog}
          value={tagFilter}
          onChange={setTagFilter}
          onManage={() => setTagManagerOpen(true)}
        />
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-44" data-testid="contacts-sort">
            <ArrowUpDown className="w-3.5 h-3.5 mr-1.5 text-[#82828c]" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="recently_added">Recently added</SelectItem>
            <SelectItem value="recently_updated">Recently updated</SelectItem>
            <SelectItem value="first_asc">First name A–Z</SelectItem>
            <SelectItem value="first_desc">First name Z–A</SelectItem>
            <SelectItem value="last_asc">Last name A–Z</SelectItem>
            <SelectItem value="last_desc">Last name Z–A</SelectItem>
            <SelectItem value="email_asc">Email A–Z</SelectItem>
            <SelectItem value="email_desc">Email Z–A</SelectItem>
            <SelectItem value="tag_asc">Tag A–Z</SelectItem>
            <SelectItem value="tag_desc">Tag Z–A</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Archive view pills — Current / Archived / All. Drives the
          backend `view` query param on GET /contacts. Default Current
          so the page opens to live contacts only; toggle Archived to
          surface soft-deleted rows so they can be restored, or All for
          a single combined view. */}
      <div className="flex flex-wrap items-center gap-2" data-testid="contacts-view-pills">
        {[
          { k: "current",  label: "Current" },
          { k: "archived", label: "Archived" },
          { k: "all",      label: "All" },
        ].map((p) => {
          const active = view === p.k;
          return (
            <button
              key={p.k}
              type="button"
              onClick={() => setView(p.k)}
              className={`text-xs px-3 py-1.5 transition-colors ${active ? "bg-[#005e7a] text-white" : "bg-[#f5efe6] text-[#4a4a52] hover:bg-[#ebe3d1]"}`}
              style={{ borderRadius: 9999 }}
              data-testid={`contacts-view-${p.k}`}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {selected.size > 0 && (
        <div
          className="bloq-card p-3 flex flex-wrap items-center gap-2 border border-[#005e7a]/30 bg-[#005e7a]/5"
          data-testid="contacts-bulk-bar"
        >
          <span className="text-xs font-medium text-[#1a1a1f]" data-testid="contacts-bulk-count">
            {selected.size} selected
          </span>
          <button
            onClick={() => setSelected(new Set())}
            className="text-xs text-[#82828c] hover:text-[#1a1a1f] underline"
            data-testid="contacts-bulk-clear"
          >
            Clear
          </button>
          <span className="flex-1" />
          <button
            disabled={bulkBusy}
            onClick={() => setBulkTagOpen(true)}
            className="bloq-btn-secondary text-xs inline-flex items-center gap-1.5 disabled:opacity-60"
            data-testid="contacts-bulk-tag"
          >
            <Tag className="w-3.5 h-3.5" /> Edit tags
          </button>
          <button
            disabled={bulkBusy}
            onClick={bulkDelete}
            className="text-xs px-3 py-1.5 bg-[#c43838] text-white hover:bg-[#a82c2c] transition-colors inline-flex items-center gap-1.5 disabled:opacity-60"
            style={{ borderRadius: 9999 }}
            data-testid="contacts-bulk-delete"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete {selected.size}
          </button>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="bloq-card p-10 text-center">
          <p className="text-sm text-[#82828c]">
            {search
              ? `No contacts match "${search}".`
              : "Contacts are auto-created when someone books a meeting with you. Add manual ones from here."}
          </p>
        </div>
      ) : (
        <div className="bloq-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-[#82828c]">
                <th className="px-4 py-3 w-8">
                  <input
                    type="checkbox"
                    className="w-3.5 h-3.5 accent-[#005e7a] cursor-pointer"
                    checked={filtered.length > 0 && filtered.every((c) => selected.has(c.contact_id))}
                    ref={(el) => {
                      if (!el) return;
                      const some = filtered.some((c) => selected.has(c.contact_id));
                      const all = filtered.every((c) => selected.has(c.contact_id));
                      el.indeterminate = some && !all;
                    }}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelected(new Set([...selected, ...filtered.map((c) => c.contact_id)]));
                      } else {
                        const next = new Set(selected);
                        filtered.forEach((c) => next.delete(c.contact_id));
                        setSelected(next);
                      }
                    }}
                    data-testid="contacts-select-all"
                    aria-label="Select all visible"
                  />
                </th>
                <th className="px-4 py-3 w-8"></th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Company</th>
                <th className="px-4 py-3">Tags</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Notes</th>
                <th className="px-4 py-3 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr
                  key={c.contact_id}
                  className={`hover:bg-[#f5efe6] cursor-pointer ${selected.has(c.contact_id) ? "bg-[#005e7a]/5" : ""}`}
                  data-testid={`contact-row-${c.contact_id}`}
                  onClick={() => { setEditing(c); setShowDialog(true); }}
                >
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className="w-3.5 h-3.5 accent-[#005e7a] cursor-pointer"
                      checked={selected.has(c.contact_id)}
                      onChange={() => {
                        const next = new Set(selected);
                        if (next.has(c.contact_id)) next.delete(c.contact_id);
                        else next.add(c.contact_id);
                        setSelected(next);
                      }}
                      data-testid={`contact-select-${c.contact_id}`}
                      aria-label={`Select ${c.name}`}
                    />
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => toggleClient(c)}
                      className="p-1.5 rounded hover:bg-white"
                      title={c.is_client ? "Remove client flag" : "Mark as client"}
                      data-testid={`toggle-client-${c.contact_id}`}
                    >
                      <Star
                        className="w-3.5 h-3.5"
                        style={{
                          fill: c.is_client ? "#005e7a" : "transparent",
                          color: c.is_client ? "#005e7a" : "#82828c",
                        }}
                      />
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{c.name}</span>
                      {c.is_client && (
                        <Link
                          to="/app/bookings?tab=clients"
                          className="text-[10px] text-[#005e7a] uppercase tracking-wider font-semibold underline"
                          data-testid={`client-badge-${c.contact_id}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          Client
                        </Link>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">{c.email}</td>
                  <td className="px-4 py-3 text-[#4a4a52]">{c.company || "—"}</td>
                  <td className="px-4 py-3" data-testid={`contact-tags-${c.contact_id}`}>
                    {Array.isArray(c.tags) && c.tags.length ? (
                      <div className="flex flex-wrap gap-1">
                        {c.tags
                          .slice()
                          .sort((a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: "base" }))
                          .slice(0, 4)
                          .map((tname) => {
                          const cat = tagCatalog.find((t) => t.name === tname);
                          const color = cat?.color || "#82828c";
                          return (
                            <span
                              key={tname}
                              className="text-[9px] font-medium uppercase tracking-wider px-2 py-0.5 leading-none"
                              style={{ borderRadius: 9999, background: `${color}22`, color }}
                            >
                              {tname}
                            </span>
                          );
                        })}
                        {c.tags.length > 4 && (
                          <span className="text-[10px] text-[#82828c]">+{c.tags.length - 4}</span>
                        )}
                      </div>
                    ) : <span className="text-[#82828c]">—</span>}
                  </td>
                  <td className="px-4 py-3"><span className="bloq-pill" style={{ background: "rgba(0,94,122,0.08)" }}>{c.source}</span></td>
                  <td className="px-4 py-3 text-[#4a4a52] max-w-xs truncate" title={c.internal_notes}>{c.internal_notes || "—"}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => { setEditing(c); setShowDialog(true); }} className="p-1.5 rounded hover:bg-white" data-testid={`edit-contact-${c.contact_id}`}><Pencil className="w-3.5 h-3.5 text-[#82828c]" /></button>
                      {c.is_archived ? (
                        <button onClick={() => restore(c)} className="p-1.5 rounded hover:bg-white" title="Restore" data-testid={`restore-contact-${c.contact_id}`}><ArchiveRestore className="w-3.5 h-3.5 text-[#005e7a]" /></button>
                      ) : (
                        <button onClick={() => archive(c)} className="p-1.5 rounded hover:bg-white" title="Archive" data-testid={`archive-contact-${c.contact_id}`}><Archive className="w-3.5 h-3.5 text-[#82828c]" /></button>
                      )}
                      <button onClick={() => del(c)} className="p-1.5 rounded hover:bg-white text-[#c43838]" data-testid={`delete-contact-${c.contact_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ContactDialog open={showDialog} onOpenChange={(o) => { setShowDialog(o); if (!o) setEditing(null); }} contact={editing} tagCatalog={tagCatalog} onSaved={() => { load(); setShowDialog(false); setEditing(null); }} />
      <TagManagerDialog open={tagManagerOpen} onClose={() => { setTagManagerOpen(false); loadTags(); load(); }} workspaceId={currentWorkspace?.workspace_id} />
      <BulkTagDialog
        open={bulkTagOpen}
        onClose={() => setBulkTagOpen(false)}
        workspaceId={currentWorkspace?.workspace_id}
        contactIds={Array.from(selected)}
        catalog={tagCatalog}
        onApplied={() => { setSelected(new Set()); load(); }}
      />
      <ImportContactsDialog
        open={importOpen}
        onClose={(refresh) => { setImportOpen(false); if (refresh) { load(); loadTags(); } }}
        workspaceId={currentWorkspace?.workspace_id}
      />
    </div>
  );
}

export function ContactDialog({ open, onOpenChange, contact, onSaved, tagCatalog: tagCatalogProp }) {
  const { currentWorkspace } = useWorkspace();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [birthday, setBirthday] = useState("");
  const [pronounMode, setPronounMode] = useState("none"); // none | one of PRONOUN_OPTIONS | other
  const [pronounsCustom, setPronounsCustom] = useState("");
  const [address, setAddress] = useState({ ...EMPTY_ADDRESS });
  const [internalNotes, setInternalNotes] = useState("");
  const [notes, setNotes] = useState("");
  const [tags, setTags] = useState([]);
  // When the dialog is opened standalone (e.g. from ClientsPanel) without
  // a parent-supplied catalog, fall back to fetching one ourselves so the
  // tag picker still works.
  const [localCatalog, setLocalCatalog] = useState([]);
  const tagCatalog = tagCatalogProp && tagCatalogProp.length ? tagCatalogProp : localCatalog;
  useEffect(() => {
    if (!open) return;
    if (tagCatalogProp && tagCatalogProp.length) return;
    const wsId = currentWorkspace?.workspace_id;
    if (!wsId) return;
    api.get(`/booking/tags`, { params: { workspace_id: wsId } })
      .then((r) => setLocalCatalog(r.data || []))
      .catch(() => setLocalCatalog([]));
  }, [open, currentWorkspace, tagCatalogProp]);
  // Soft client-flag toggle — flips an existing contact into a client
  // immediately on save. For brand-new contacts, we promote after the
  // POST returns with a contact_id.
  const [isClient, setIsClient] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (contact) {
      const fn = contact.first_name || (contact.name || "").split(" ")[0] || "";
      const ln = contact.last_name || ((contact.name || "").split(" ").slice(1).join(" ")) || "";
      setFirstName(fn); setLastName(ln);
      setEmail(contact.email || "");
      setPhone(contact.phone || "");
      setCompany(contact.company || "");
      setNotes(contact.notes || "");
      setInternalNotes(contact.internal_notes || "");
      setBirthday(contact.birthday || "");
      const p = (contact.pronouns || "").toLowerCase();
      if (!p) { setPronounMode("none"); setPronounsCustom(""); }
      else if (PRONOUN_OPTIONS.includes(p)) { setPronounMode(p); setPronounsCustom(""); }
      else { setPronounMode("other"); setPronounsCustom(contact.pronouns); }
      setAddress(contact.address && (contact.address.line1 || contact.address.city) ? { ...EMPTY_ADDRESS, ...contact.address } : { ...EMPTY_ADDRESS });
      setIsClient(!!contact.is_client);
      setTags(Array.isArray(contact.tags) ? contact.tags : []);
    } else {
      setFirstName(""); setLastName(""); setEmail(""); setPhone(""); setCompany("");
      setNotes(""); setInternalNotes(""); setBirthday(""); setPronounMode("none"); setPronounsCustom("");
      setAddress({ ...EMPTY_ADDRESS });
      setIsClient(false);
      setTags([]);
    }
  }, [contact, open]);

  const submit = async () => {
    if (!firstName.trim() || !email.trim()) {
      toast.error("First name and email are required");
      return;
    }
    let pronouns = "";
    if (pronounMode === "other") pronouns = pronounsCustom.trim();
    else if (pronounMode !== "none") pronouns = pronounMode;

    const body = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email,
      phone,
      company,
      notes,
      birthday: birthday || null,
      pronouns,
      address,
      internal_notes: internalNotes,
      tags,
    };
    setSubmitting(true);
    try {
      let saved;
      if (contact) {
        const r = await api.patch(`/contacts/${contact.contact_id}`, body);
        saved = r.data;
      } else {
        const r = await api.post(`/contacts`, { ...body, workspace_id: currentWorkspace.workspace_id });
        saved = r.data;
      }
      // Sync the soft client flag — only call promote/demote when the
      // current state actually differs from the toggle, so we don't
      // mash client_since on every edit.
      try {
        const ws = currentWorkspace?.workspace_id;
        if (ws && saved?.contact_id) {
          const wasClient = !!contact?.is_client;
          if (isClient && !wasClient) {
            await api.post(`/booking/clients/${saved.contact_id}/promote`, null, { params: { workspace_id: ws } });
          } else if (!isClient && wasClient) {
            await api.post(`/booking/clients/${saved.contact_id}/demote`, null, { params: { workspace_id: ws } });
          }
        }
      } catch { /* non-fatal */ }
      toast.success(contact ? "Contact updated" : "Contact added");
      onSaved?.(saved);
    } catch {
      toast.error("Could not save");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{contact ? "Edit contact" : "New contact"}</DialogTitle></DialogHeader>

        {/* Mark-as-client toggle — flips the soft `is_client` flag on
            save. New contacts get promoted right after POST returns;
            existing contacts diff against `contact.is_client` so we
            don't mash client_since on noisy saves. */}
        <label
          className="flex items-center gap-3 mt-3 p-3 bg-[#f5efe6] rounded-md cursor-pointer select-none"
          data-testid="contact-is-client-toggle"
        >
          <input
            type="checkbox"
            checked={isClient}
            onChange={(e) => setIsClient(e.target.checked)}
            className="w-4 h-4 accent-[#005e7a]"
          />
          <span className="text-sm flex-1">
            <span className="font-medium text-[#1a1a1f]">Mark as client</span>
            <span className="text-[#82828c] block text-xs mt-0.5">
              Clients show up in the Clients tab with extended profile sections (status, address, tags, lifetime value).
            </span>
          </span>
        </label>

        <div className="space-y-3 mt-3">
          {/* Identity */}
          <p className="text-[10px] uppercase tracking-widest text-[#82828c] font-semibold">Identity</p>
          <div className="grid grid-cols-2 gap-3">
            <LabeledField label="First name *">
              <input className="bloq-input" value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="contact-firstname-field" />
            </LabeledField>
            <LabeledField label="Last name">
              <input className="bloq-input" value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="contact-lastname-field" />
            </LabeledField>
            <LabeledField label="Email *">
              <input className="bloq-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="contact-email-field" />
            </LabeledField>
            <LabeledField label="Phone (optional)">
              <input
                className="bloq-input"
                type="tel"
                inputMode="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/[^0-9+\-()\s]/g, ""))}
                data-testid="contact-phone-field"
              />
            </LabeledField>
            <LabeledField label="Company (optional)">
              <input className="bloq-input" value={company} onChange={(e) => setCompany(e.target.value)} data-testid="contact-company-field" />
            </LabeledField>
            <LabeledField label="Birthday (optional)">
              <input className="bloq-input" type="date" value={birthday} onChange={(e) => setBirthday(e.target.value)} data-testid="contact-birthday-field" />
            </LabeledField>
            <LabeledField label="Pronouns (optional)" full={pronounMode !== "other"}>
              <Select value={pronounMode} onValueChange={setPronounMode}>
                <SelectTrigger data-testid="contact-pronouns-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Not specified —</SelectItem>
                  {PRONOUN_OPTIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  <SelectItem value="other">Other (fill in)</SelectItem>
                </SelectContent>
              </Select>
            </LabeledField>
            {pronounMode === "other" && (
              <LabeledField label="Custom pronouns">
                <input className="bloq-input" placeholder="xe/xem" value={pronounsCustom} onChange={(e) => setPronounsCustom(e.target.value)} data-testid="contact-pronouns-custom" />
              </LabeledField>
            )}
          </div>

          {/* Address */}
          <p className="text-[10px] uppercase tracking-widest text-[#82828c] font-semibold pt-3">Address (optional)</p>
          <AddressFields value={address} onChange={setAddress} />

          {/* Tags — picker from workspace catalog. Empty when no tags
              exist; user adds them via the Manage tags dialog. */}
          <p className="text-[10px] uppercase tracking-widest text-[#82828c] font-semibold pt-3">Tags</p>
          {tagCatalog.length === 0 ? (
            <p className="text-xs text-[#82828c]">
              No tags yet. Create some via the <strong>Manage tags</strong> button on the Contacts page.
            </p>
          ) : (
            <TagPicker catalog={tagCatalog} value={tags} onChange={setTags} />
          )}

          {/* Notes */}
          <p className="text-[10px] uppercase tracking-widest text-[#82828c] font-semibold pt-3">Notes</p>
          <LabeledField label="Public note (visible if shared)" full>
            <textarea className="bloq-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="contact-notes-field" />
          </LabeledField>
          <LabeledField label="Internal notes (host-only)" full>
            <textarea className="bloq-input" rows={3} value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)} data-testid="contact-internal-notes-field" />
          </LabeledField>
        </div>

        <DialogFooter className="mt-4">
          <button className="bloq-btn-secondary" onClick={() => onOpenChange(false)}>Cancel</button>
          <button className="bloq-btn-primary disabled:opacity-60" disabled={submitting} onClick={submit} data-testid="contact-save-button">
            {submitting ? "Saving…" : (contact ? "Save changes" : "Add contact")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LabeledField({ label, full, children }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="text-xs uppercase tracking-wider text-[#82828c] block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ImportContactsDialog — upload an .xlsx (matching the downloadable
// template) or a CSV and bulk-create contacts. Server dedups by email so
// re-importing the same workbook is safe; the response summary surfaces
// per-row errors for anything that didn't make it in.
// ---------------------------------------------------------------------------
function ImportContactsDialog({ open, onClose, workspaceId }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { imported, skipped, errors: [{row,reason}] }
  const fileRef = useRef(null);

  const reset = () => { setResult(null); if (fileRef.current) fileRef.current.value = ""; };

  const submit = async (e) => {
    e?.preventDefault?.();
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose a file first"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/contacts/import", fd, {
        params: { workspace_id: workspaceId },
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      const imp = r.data?.imported || 0;
      const mer = r.data?.merged || 0;
      if (imp > 0 || mer > 0) {
        const parts = [];
        if (imp) parts.push(`${imp} new`);
        if (mer) parts.push(`${mer} merged`);
        toast.success(`Imported ${parts.join(" + ")}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(!!result?.imported); } }}>
      <DialogContent className="max-w-md" data-testid="import-contacts-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Upload className="w-5 h-5 text-[#005e7a]" /> Import contacts</DialogTitle>
        </DialogHeader>

        <p className="text-xs text-[#82828c] -mt-1">
          Upload an Excel workbook (.xlsx) using the Bloq template, or a plain CSV. The first sheet of the
          workbook should contain your contact rows; column headers are matched case-insensitively. Email is the
          unique key — duplicates <strong>merge</strong> (non-empty cells override, empty cells leave existing
          values untouched), so re-importing a partially filled-in file is safe and is the recommended way to bulk-edit.
        </p>

        {!result ? (
          <form onSubmit={submit} className="space-y-3 mt-3">
            <input
              type="file"
              ref={fileRef}
              accept=".xlsx,.csv,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
              className="bloq-input file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:bg-[#f5efe6] file:text-[#1a1a1f]"
              data-testid="import-contacts-file"
            />
            <DialogFooter>
              <button type="button" className="bloq-btn-secondary text-sm" onClick={() => { reset(); onClose(false); }}>Cancel</button>
              <button type="submit" className="bloq-btn-primary text-sm disabled:opacity-60" disabled={busy} data-testid="import-contacts-submit">
                {busy ? "Importing…" : "Import"}
              </button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-3 mt-3" data-testid="import-contacts-result">
            <div className="grid grid-cols-3 gap-3">
              <div className="bloq-card p-3">
                <p className="text-[10px] uppercase tracking-widest text-[#82828c] font-semibold">New</p>
                <p className="bloq-display text-2xl mt-1 text-[#005e7a]">{result.imported || 0}</p>
              </div>
              <div className="bloq-card p-3">
                <p className="text-[10px] uppercase tracking-widest text-[#82828c] font-semibold">Merged</p>
                <p className="bloq-display text-2xl mt-1 text-[#15803d]">{result.merged || 0}</p>
              </div>
              <div className="bloq-card p-3">
                <p className="text-[10px] uppercase tracking-widest text-[#82828c] font-semibold">Skipped</p>
                <p className="bloq-display text-2xl mt-1 text-[#82828c]">{result.skipped || 0}</p>
              </div>
            </div>
            {Array.isArray(result.errors) && result.errors.length > 0 && (
              <div className="border border-[#fbbf24] bg-[#fffbeb] rounded-md p-3 max-h-40 overflow-y-auto">
                <p className="text-[10px] uppercase tracking-widest text-[#b45309] font-semibold mb-1">
                  Rows skipped ({result.errors.length})
                </p>
                <ul className="text-xs text-[#92400e] space-y-0.5">
                  {result.errors.slice(0, 20).map((er, i) => (
                    <li key={i} data-testid={`import-error-${i}`}>
                      Row {er.row}: {er.reason}
                    </li>
                  ))}
                  {result.errors.length > 20 && (
                    <li className="italic">…and {result.errors.length - 20} more.</li>
                  )}
                </ul>
              </div>
            )}
            <DialogFooter>
              <button type="button" className="bloq-btn-secondary text-sm" onClick={reset}>Import another</button>
              <button
                type="button"
                className="bloq-btn-primary text-sm"
                onClick={() => { const r = result; reset(); onClose(!!r?.imported); }}
                data-testid="import-contacts-done"
              >Done</button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
